import type { RuleGroupView, RuleStatus, RuleView } from "../../shared/api.js";
import { Page } from "../Page.js";
import { importRules } from "../components/ImportDialog.js";
import { describeMatch, editGroup } from "../components/GroupEditor.js";
import { editRule } from "../components/RuleEditor.js";
import { debounce, h, patch } from "../dom.js";
import { ago, amount, compact, full, stackDetail, stackName } from "../format.js";
import { stackIcon } from "../components/StackIcon.js";
import { favoriteStar } from "../components/Favorite.js";
import { ViewToggle } from "../components/ViewToggle.js";

const PAGE_SIZE = 100;

const STATUS_LABEL: Record<RuleStatus, string> = {
  ok: "OK",
  low: "Low",
  crafting: "Crafting",
  backoff: "Backing off",
  disabled: "Disabled",
  unknown: "No data",
};

/**
 * Rules with live stock and the rule groups that set many of them, as cards or
 * a table with the favorites first: search, filter, inline toggles, bulk
 * actions, import and export.
 */
export class StockPage extends Page {
  readonly title = "Stock";
  private rows: RuleView[] = [];
  private groups: RuleGroupView[] = [];
  private total = 0;
  private offset = 0;
  private readonly selected = new Set<number>();

  private readonly search = h("input", { type: "search", placeholder: "Search rules…" });
  private readonly type = h("select", null, h("option", { value: "" }, "All types"), h("option", { value: "item" }, "Items"), h("option", { value: "fluid" }, "Fluids"), h("option", { value: "essentia" }, "Essentia"));
  private readonly status = h("select", null, h("option", { value: "" }, "Any status"), ...Object.entries(STATUS_LABEL).map(([v, l]) => h("option", { value: v }, l)));
  private readonly group = h("select", null, h("option", { value: "" }, "All rules"));
  private readonly sort = h("select", null, h("option", { value: "status" }, "Sort: needs attention"), h("option", { value: "name" }, "Sort: name"), h("option", { value: "priority" }, "Sort: priority"), h("option", { value: "fill" }, "Sort: fill level"), h("option", { value: "recent" }, "Sort: recently edited"));
  private readonly tbody = h("tbody");
  private readonly view = new ViewToggle("stock", () => this.render());
  private readonly selectPage = h("input", { type: "checkbox", onchange: (e: Event) => this.selectAll((e.target as HTMLInputElement).checked) });
  private readonly table: HTMLElement = h(
    "div",
    { class: "table-wrap" },
    h(
      "table",
      { class: "rules" },
      h(
        "thead",
        null,
        h(
          "tr",
          null,
          h("th", { class: "check" }, h("input", { type: "checkbox", "aria-label": "Select all", onchange: (e: Event) => this.selectAll((e.target as HTMLInputElement).checked) })),
          h("th", null, "Stack"),
          h("th", { class: "stock-col" }, "Stock / min → target"),
          h("th", { class: "num" }, "Batch"),
          h("th", { class: "num" }, "Prio"),
          h("th", null, "Status"),
          h("th", null, "On"),
          h("th", { title: "Critical: stocked by the computer while the app is offline" }, "Crit"),
          h("th", null, ""),
        ),
      ),
      this.tbody,
    ),
  );
  private readonly grid = h("div", { class: "mini-grid rule-grid" });
  private readonly listBox = h("div");
  private readonly pager = h("div", { class: "pager" });
  private readonly bulkBar = h("div", { class: "bulk-bar hidden" });
  private readonly summary = h("span", { class: "muted" });

  mount(): void {
    const reload = debounce(() => {
      this.offset = 0;
      void this.load();
    }, 200);
    this.search.addEventListener("input", reload);
    for (const s of [this.type, this.status, this.group, this.sort]) s.addEventListener("change", reload);
    void this.loadGroups();
    const refetchGroups = debounce(() => void this.loadGroups(), 600);
    this.listen("groups", refetchGroups);
    this.listen("rules", refetchGroups);
    this.listen("jobs", refetchGroups);

    const exportMenu = h(
      "details",
      { class: "menu" },
      h("summary", { class: "button" }, "Export"),
      h("div", { class: "menu-items" }, h("a", { href: "/api/rules/export?format=csv" }, "CSV"), h("a", { href: "/api/rules/export?format=json" }, "JSON")),
    );

    this.el.append(
      h(
        "div",
        { class: "toolbar" },
        this.search,
        this.type,
        this.status,
        this.group,
        this.sort,
        h("span", { class: "spacer" }),
        this.view.el,
        h("button", { onclick: () => void this.import() }, "Import"),
        exportMenu,
        h("button", { title: "One rule for many stacks, like all essentia at 5000 or all circuits at 512", onclick: () => void this.editGroup(null) }, "+ Add group"),
        h("button", { class: "primary", onclick: () => void this.add() }, "+ Add rule"),
      ),
      this.bulkBar,
      this.listBox,
      h("div", { class: "table-foot" }, this.summary, this.pager),
    );

    this.listen("stock", debounce(() => this.patchStock(), 250));
    this.listen("favorites", debounce(() => void this.load(), 300));
    const refetch = debounce(() => void this.load(), 600);
    this.listen("rules", refetch);
    this.listen("jobs", refetch);
    this.listen("snapshot", refetch);
    void this.load();
  }

  private async load(): Promise<void> {
    const page = await this.attempt(() =>
      this.ctx.api.rules({
        q: this.search.value,
        type: this.type.value,
        status: this.status.value,
        group: this.group.value,
        sort: this.sort.value,
        offset: this.offset,
        limit: PAGE_SIZE,
      }),
    );
    if (!page) return;
    this.rows = page.items;
    this.total = page.total;
    this.render();
  }

  /** Loads the groups for their rows and the group filter, keeping the current choice. */
  private async loadGroups(): Promise<void> {
    const groups = await this.attempt(() => this.ctx.api.groups());
    if (!groups) return;
    this.groups = groups;
    const current = this.group.value;
    patch(this.group, 
      h("option", { value: "" }, "All rules"),
      h("option", { value: "own" }, "Rules of their own"),
      ...(groups.length ? [h("option", { value: "groups" }, "Any group's rules")] : []),
      ...groups.map((g) => h("option", { value: String(g.id) }, `Group: ${g.name}`)),
    );
    this.group.value = [...this.group.options].some((o) => o.value === current) ? current : "";
    this.render();
  }

  /** Groups shown above the rules on the first page, under the same filters. */
  private visibleGroups(): RuleGroupView[] {
    if (this.offset !== 0) return [];
    const filter = this.group.value;
    if (filter === "own") return [];
    const q = this.search.value.trim().toLowerCase();
    const status = this.status.value;
    return this.groups.filter(
      (g) =>
        (filter === "" || filter === "groups" || filter === String(g.id)) &&
        (!q || g.name.toLowerCase().includes(q)) &&
        (!this.type.value || !g.match.type || g.match.type === this.type.value) &&
        (!status || groupStatus(g) === status),
    );
  }

  private render(): void {
    const groups = this.visibleGroups();
    const cards = this.view.cards;
    const empty = this.total === 0 && !this.search.value ? "No rules yet. Add one, or import a list." : "No rule matches.";
    if (cards) {
      this.selectPage.checked = this.rows.length > 0 && this.rows.every((r) => this.selected.has(r.id));
      patch(this.grid, ...groups.map((g) => this.renderGroupCard(g)), ...this.rows.map((r) => this.renderCard(r)));
      if (!this.rows.length && !groups.length) patch(this.listBox, h("p", { class: "muted empty" }, empty));
      else patch(this.listBox, this.rows.length ? h("label", { class: "check select-page small muted" }, this.selectPage, "Select this page") : null, this.grid);
    } else {
      patch(
        this.tbody,
        ...groups.map((g) => this.renderGroupRow(g)),
        ...this.rows.map((r) => this.renderRow(r)),
        !this.rows.length && !groups.length ? h("tr", null, h("td", { colspan: "9", class: "empty" }, empty)) : null,
      );
      patch(this.listBox, this.table);
    }
    const end = Math.min(this.offset + PAGE_SIZE, this.total);
    this.summary.textContent = this.total ? `${this.offset + 1}–${end} of ${full(this.total)} rules` : "";
    patch(
      this.pager,
      ...(this.total > PAGE_SIZE
        ? [
            h("button", { disabled: this.offset === 0, onclick: () => this.goto(this.offset - PAGE_SIZE) }, "‹ Prev"),
            h("button", { disabled: end >= this.total, onclick: () => this.goto(this.offset + PAGE_SIZE) }, "Next ›"),
          ]
        : []),
    );
    this.renderBulkBar();
  }

  /** A group as a mini card: its name, what it covers, how many rules are low, and its switches. */
  private renderGroupCard(g: RuleGroupView): HTMLElement {
    const status = groupStatus(g);
    return h(
      "article",
      { key: `group:${g.id}`, class: `mini-card group-card status-${status}` },
      h("div", { class: "mc-head" }, h("span", { class: "slot group-slot", "aria-hidden": "true" }, "▤"), h("span", { class: "mc-name" }, g.name), h("span", { class: "badge group" }, "Group")),
      h("div", { class: "muted small mc-detail" }, describeMatch(g.match), g.exclude.length ? ` · ${g.exclude.length} skipped` : ""),
      h(
        "div",
        { class: "small" },
        h("button", { class: "link", title: "Show this group's rules", onclick: () => this.showGroup(g) }, `${full(g.members)} rules`),
        h("span", { class: "muted" }, ` · ${compact(g.min)} → ${compact(g.target)} each`),
      ),
      h(
        "div",
        { class: "mc-foot" },
        h("span", { class: `badge ${status}` }, status === "low" ? `${g.low} low` : STATUS_LABEL[status]),
        g.crafting ? h("span", { class: "muted small" }, `${g.crafting} crafting`) : null,
      ),
      h(
        "div",
        { class: "mc-actions" },
        h("label", { class: "check small", title: "Enabled" }, this.groupToggle(g, "enabled"), "On"),
        h("label", { class: "check small", title: "Critical: stocked by the computer while the app is offline" }, this.groupToggle(g, "critical"), "Crit"),
        h("span", { class: "spacer" }),
        h("button", { class: "small", onclick: () => void this.editGroup(g) }, "Edit"),
        h("button", { class: "small danger", title: "Delete", "aria-label": `Delete the group ${g.name}`, onclick: () => void this.removeGroup(g) }, "✕"),
      ),
    );
  }

  private groupToggle(g: RuleGroupView, field: "enabled" | "critical"): HTMLInputElement {
    return h("input", {
      type: "checkbox",
      checked: g[field],
      "aria-label": field,
      onchange: async (e: Event) => {
        const box = e.target as HTMLInputElement;
        const ok = await this.attempt(() => this.ctx.api.updateGroup(g.id, { [field]: box.checked }));
        if (!ok) box.checked = !box.checked;
      },
    });
  }

  private showGroup(g: RuleGroupView): void {
    this.group.value = String(g.id);
    this.offset = 0;
    void this.load();
  }

  private renderGroupRow(g: RuleGroupView): HTMLTableRowElement {
    const toggle = (field: "enabled" | "critical") => this.groupToggle(g, field);
    const status = groupStatus(g);
    const showRules = () => this.showGroup(g);
    return h(
      "tr",
      { key: `group:${g.id}`, class: `group-row status-${status}` },
      h("td", { class: "check" }),
      h(
        "td",
        null,
        h("div", { class: "stack-name" }, h("span", { class: "badge group" }, "Group"), g.name),
        h("div", { class: "muted small" }, describeMatch(g.match), g.exclude.length ? ` · ${g.exclude.length} skipped` : ""),
      ),
      h(
        "td",
        { class: "stock-col" },
        h("div", { class: "stock-text" }, h("button", { class: "link", title: "Show this group's rules", onclick: showRules }, `${full(g.members)} rules`), h("span", { class: "muted" }, ` / ${compact(g.min)} → ${compact(g.target)} each`)),
      ),
      h("td", { class: "num" }, compact(g.batch)),
      h("td", { class: "num" }, String(g.priority)),
      h(
        "td",
        null,
        h("span", { class: `badge ${status}` }, status === "low" ? `${g.low} low` : STATUS_LABEL[status]),
        g.crafting ? h("div", { class: "muted small" }, `${g.crafting} crafting`) : null,
      ),
      h("td", null, toggle("enabled")),
      h("td", null, toggle("critical")),
      h(
        "td",
        { class: "actions" },
        h("button", { class: "small", onclick: () => void this.editGroup(g) }, "Edit"),
        h("button", { class: "small danger", onclick: () => void this.removeGroup(g) }, "Delete"),
      ),
    );
  }

  private goto(offset: number): void {
    this.offset = Math.max(0, offset);
    void this.load();
  }

  private ruleToggle(rule: RuleView, field: "enabled" | "critical"): HTMLInputElement {
    return h("input", {
      type: "checkbox",
      checked: rule[field],
      "aria-label": field,
      onchange: async (e: Event) => {
        const box = e.target as HTMLInputElement;
        const ok = await this.attempt(() => this.ctx.api.updateRule(rule.id, { [field]: box.checked }));
        if (!ok) box.checked = !box.checked;
      },
    });
  }

  private selectBox(rule: RuleView): HTMLInputElement {
    return h("input", {
      type: "checkbox",
      class: "select",
      "aria-label": `Select ${stackName(rule.stack)}`,
      checked: this.selected.has(rule.id),
      onchange: (e: Event) => this.toggleSelect(rule.id, (e.target as HTMLInputElement).checked),
    });
  }

  /** A rule as a mini card: picture, name, stock against min and target, status, switches and actions. */
  private renderCard(rule: RuleView): HTMLElement {
    const failure = rule.lastFailure ? `Last failure ${ago(rule.lastFailure.at)}: ${rule.lastFailure.reason}` : "";
    return h(
      "article",
      { key: rule.key, class: `mini-card status-${rule.status}${this.ctx.live.favorites.has(rule.key) ? " favorite" : ""}`, title: stackDetail(rule.stack) },
      h(
        "div",
        { class: "mc-head" },
        h("span", { class: "slot" }, stackIcon(rule.stack, "lg")),
        h("span", { class: "mc-name" }, stackName(rule.stack)),
        favoriteStar(this.ctx, rule.key, rule.stack),
      ),
      rule.groupName ? h("div", null, h("span", { class: "badge group", title: "Set by this group. Editing it here makes it a rule of its own." }, rule.groupName)) : null,
      h("div", { class: "stock-slot" }, this.stockCell(rule)),
      h(
        "div",
        { class: "mc-foot" },
        h("span", { class: `badge ${rule.status}`, title: failure }, STATUS_LABEL[rule.status]),
        h("span", { class: "muted small", title: "Batch and priority" }, `×${amount(rule.batch, rule.stack.type)} · p${rule.priority}`),
      ),
      failure ? h("div", { class: "small error mc-detail", title: failure }, rule.lastFailure!.reason) : null,
      h(
        "div",
        { class: "mc-actions" },
        this.selectBox(rule),
        h("label", { class: "check small", title: "Enabled" }, this.ruleToggle(rule, "enabled"), "On"),
        h("label", { class: "check small", title: "Critical: stocked by the computer while the app is offline" }, this.ruleToggle(rule, "critical"), "Crit"),
        h("span", { class: "spacer" }),
        h("button", { class: "small", title: "Craft the batch now", onclick: () => void this.craftNow(rule) }, "Craft"),
        h("button", { class: "small", onclick: () => void this.edit(rule) }, "Edit"),
        h("button", { class: "small danger", title: "Delete", "aria-label": `Delete the rule for ${stackName(rule.stack)}`, onclick: () => void this.remove(rule) }, "✕"),
      ),
    );
  }

  private renderRow(rule: RuleView): HTMLTableRowElement {
    const toggle = (field: "enabled" | "critical") => this.ruleToggle(rule, field);
    const failure = rule.lastFailure ? `Last failure ${ago(rule.lastFailure.at)}: ${rule.lastFailure.reason}` : "";
    const backoff = rule.backoffUntil ? ` · retry ${new Date(rule.backoffUntil).toLocaleTimeString()}` : "";
    return h(
      "tr",
      { key: rule.key, class: `status-${rule.status}` },
      h("td", { class: "check" }, this.selectBox(rule)),
      h(
        "td",
        null,
        h("div", { class: "stack-name" }, favoriteStar(this.ctx, rule.key, rule.stack), stackIcon(rule.stack), stackName(rule.stack), rule.groupName ? h("span", { class: "badge group", title: "Set by this group. Editing it here makes it a rule of its own." }, rule.groupName) : null),
        h("div", { class: "muted small" }, stackDetail(rule.stack)),
      ),
      h("td", { class: "stock-col stock-slot" }, this.stockCell(rule)),
      h("td", { class: "num" }, amount(rule.batch, rule.stack.type)),
      h("td", { class: "num" }, String(rule.priority)),
      h("td", null, h("span", { class: `badge ${rule.status}`, title: failure }, STATUS_LABEL[rule.status]), failure ? h("div", { class: "muted small", title: failure }, rule.lastFailure!.reason + backoff) : null),
      h("td", null, toggle("enabled")),
      h("td", null, toggle("critical")),
      h(
        "td",
        { class: "actions" },
        h("button", { class: "small", title: "Craft the batch now", onclick: () => void this.craftNow(rule) }, "Craft"),
        h("button", { class: "small", onclick: () => void this.edit(rule) }, "Edit"),
        h("button", { class: "small danger", onclick: () => void this.remove(rule) }, "Delete"),
      ),
    );
  }

  private stockCell(rule: RuleView): HTMLElement {
    const stock = rule.stock;
    const pct = stock === null || rule.target === 0 ? 0 : Math.min(100, (stock / rule.target) * 100);
    const minPct = rule.target === 0 ? 0 : Math.min(100, (rule.min / rule.target) * 100);
    return h(
      "div",
      { class: "stock-cell" },
      h("div", { class: "stock-text" }, h("strong", { title: stock === null ? "" : full(stock) }, amount(stock, rule.stack.type)), h("span", { class: "muted" }, ` / ${amount(rule.min, rule.stack.type)} → ${amount(rule.target, rule.stack.type)}`)),
      h("div", { class: "bar" }, h("div", { class: `fill ${stock !== null && stock < rule.min ? "low" : ""}`, style: `width:${pct}%` }), h("div", { class: "min-mark", style: `left:${minPct}%` })),
    );
  }

  /** Live stock updates patch the visible rows without a refetch. */
  private patchStock(): void {
    let changed = false;
    for (const rule of this.rows) {
      const value = this.ctx.live.stock.get(rule.key);
      if (value === undefined || rule.stock === value) continue;
      rule.stock = value;
      changed = true;
    }
    this.ctx.live.stock.clear();
    if (changed) this.render();
  }

  private toggleSelect(id: number, on: boolean): void {
    if (on) this.selected.add(id);
    else this.selected.delete(id);
    this.renderBulkBar();
  }

  private selectAll(on: boolean): void {
    for (const r of this.rows) on ? this.selected.add(r.id) : this.selected.delete(r.id);
    for (const box of this.listBox.querySelectorAll<HTMLInputElement>("input.select")) box.checked = on;
    this.selectPage.checked = on;
    this.renderBulkBar();
  }

  private renderBulkBar(): void {
    this.bulkBar.classList.toggle("hidden", this.selected.size === 0);
    if (!this.selected.size) return patch(this.bulkBar);
    const run = (action: "enable" | "disable" | "delete") => async () => {
      if (action === "delete" && !confirm(`Delete ${this.selected.size} rules?`)) return;
      const ids = [...this.selected];
      const r = await this.attempt(() => this.ctx.api.bulkRules(ids, action));
      if (!r) return;
      this.ctx.toast(action === "delete" ? `Deleted ${r.removed} rules` : `Updated ${r.changed} rules`);
      this.selected.clear();
      await this.load();
    };
    patch(this.bulkBar, 
      h("span", null, `${this.selected.size} selected`),
      h("button", { onclick: run("enable") }, "Enable"),
      h("button", { onclick: run("disable") }, "Disable"),
      h("button", { class: "danger", onclick: run("delete") }, "Delete"),
      h("button", { class: "link", onclick: () => { this.selected.clear(); this.render(); } }, "Clear selection"),
    );
  }

  private async add(): Promise<void> {
    if (await editRule(this.ctx, null)) await this.load();
  }

  private async edit(rule: RuleView): Promise<void> {
    if (await editRule(this.ctx, rule)) await this.load();
  }

  private async remove(rule: RuleView): Promise<void> {
    const question = rule.groupName
      ? `Remove ${stackName(rule.stack)} from the group ${rule.groupName}? The group will skip it from now on.`
      : `Delete the rule for ${stackName(rule.stack)}? A group that covers it takes it back.`;
    if (!confirm(question)) return;
    if (await this.attempt(() => this.ctx.api.deleteRule(rule.id), "Rule deleted")) {
      this.selected.delete(rule.id);
      await this.load();
    }
  }

  private async editGroup(group: RuleGroupView | null): Promise<void> {
    if (await editGroup(this.ctx, group)) await Promise.all([this.loadGroups(), this.load()]);
  }

  private async removeGroup(group: RuleGroupView): Promise<void> {
    if (!confirm(`Delete the group ${group.name} and its ${group.members} rules? Rules of their own are kept.`)) return;
    if (await this.attempt(() => this.ctx.api.deleteGroup(group.id), "Group deleted")) await Promise.all([this.loadGroups(), this.load()]);
  }

  private async craftNow(rule: RuleView): Promise<void> {
    await this.attempt(() => this.ctx.api.craft(rule.stack, rule.batch), `Requested ${rule.batch} ${stackName(rule.stack)}`);
  }

  private async import(): Promise<void> {
    if (await importRules(this.ctx)) await this.load();
  }
}

/** A group's status for its row and the status filter: disabled, low when any of its rules is low, else crafting or OK. */
function groupStatus(g: RuleGroupView): RuleStatus {
  if (!g.enabled) return "disabled";
  if (g.low) return "low";
  if (g.crafting) return "crafting";
  return g.members ? "ok" : "unknown";
}
